<template>
  <div>
    <v-card class="mb-4">
      <v-card-title>Документы<v-spacer />
        <v-btn v-if="canCreate" color="success" :disabled="busy" @click="openForm(null)">Создать документ</v-btn>
      </v-card-title>
      <v-card-text v-if="listError">
        <v-alert type="error">{{ listError }}</v-alert>
        <v-btn text @click="loadList">Повторить</v-btn>
      </v-card-text>
      <v-data-table :headers="headers" :items="items" :loading="loading"
        :options.sync="options" :server-items-length="total" :footer-props="{ itemsPerPageOptions: [10, 25, 50] }">
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn text :disabled="busy" @click="openDocument(item.id)">Редакции</v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-progress-linear v-if="detailLoading" indeterminate />
    <v-alert v-if="detailError" type="error">
      {{ detailError }} <v-btn text @click="openDocument(selectedId)">Повторить</v-btn>
    </v-alert>
    <v-card v-if="document && !detailLoading" class="mb-4">
      <v-card-title>{{ document.title }}<v-spacer />
        <v-btn v-if="canUpdate" color="primary" :disabled="busy" @click="openForm(document.id)">Новая редакция</v-btn>
      </v-card-title>
      <v-card-subtitle>Тип: {{ document.type }}</v-card-subtitle>
      <v-card-text>
        <p>Опубликованные файлы и настройки редакций не изменяются. Для любых правок создайте новую редакцию.</p>
        <v-alert v-if="!document.revisions.length" type="info">Редакций пока нет.</v-alert>
        <v-card v-for="revision in document.revisions" :key="revision.revisionId" outlined class="mb-3">
          <v-card-title class="text-subtitle-1">Редакция {{ revision.revisionId }}
            <v-chip small class="ml-3">{{ statusLabel(revision.status) }}</v-chip>
          </v-card-title>
          <v-card-text>
            <div>Опубликована: {{ date(revision.publishedAt) }}</div>
            <div>Обязательное принятие: {{ revision.acceptanceRequired ? 'Да' : 'Нет' }}</div>
            <div>Повторное принятие: {{ revision.requiresReacceptance ? 'Да' : 'Нет' }}</div>
          </v-card-text>
          <v-card-actions>
            <a :href="pdfUrl(revision.pdfUrl)" target="_blank" rel="noopener noreferrer" class="mr-5">Просмотреть PDF</a>
            <v-btn v-if="canPublish && revision.status === 'DRAFT'" color="success" :disabled="busy" @click="preparePublish(revision)">Опубликовать</v-btn>
          </v-card-actions>
        </v-card>
      </v-card-text>
    </v-card>

    <v-dialog v-model="formOpen" max-width="900" persistent>
      <revision-form v-if="formOpen" :document-id="formDocumentId" @busy="formBusy = $event" @cancel="formOpen = false" @saved="saved" />
    </v-dialog>

    <v-dialog v-model="publishOpen" max-width="900" persistent>
      <v-card v-if="publishRevision">
        <v-card-title>Публикация редакции {{ publishRevision.revisionId }}</v-card-title>
        <v-card-text>
          <v-alert v-if="publishError" type="error">{{ publishError }}</v-alert>
          <a v-if="pdfUrl(publishRevision.pdfUrl)" :href="pdfUrl(publishRevision.pdfUrl)" target="_blank" rel="noopener noreferrer">Открыть PDF</a>
          <v-checkbox v-model="publication.acceptanceRequired" :disabled="publishing" label="Обязательное принятие для продолжения работы" />
          <v-checkbox v-model="publication.requiresReacceptance" :disabled="publishing" label="Повторное принятие действующими пользователями" />
          <p>После публикации PDF и параметры этой редакции нельзя изменить.</p>
          <v-checkbox v-model="reviewed" :disabled="publishing" label="Я проверил PDF и параметры публикации" />
        </v-card-text>
        <v-card-actions><v-spacer />
          <v-btn text :disabled="publishing" @click="publishOpen = false">Отмена</v-btn>
          <v-btn color="success" :loading="publishing" :disabled="!reviewed || publishing || !pdfUrl(publishRevision.pdfUrl)" @click="publish">Опубликовать</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import RevisionForm from '@/components/documents/RevisionForm.vue';

export default {
  components: { RevisionForm },
  metaInfo: { title: 'Документы' },
  data: () => ({
    items: [], total: 0, options: { page: 1, itemsPerPage: 10 }, loading: false, listError: null,
    document: null, selectedId: null, detailLoading: false, detailError: null,
    formOpen: false, formDocumentId: null, formBusy: false,
    publishOpen: false, publishRevision: null, publishing: false, publishError: null,
    publication: { acceptanceRequired: true, requiresReacceptance: false }, reviewed: false,
    listRequest: 0, detailRequest: 0,
    headers: [
      { text: 'Название', value: 'title', sortable: false },
      { text: 'Тип', value: 'type', sortable: false },
      { text: 'Действия', value: 'actions', sortable: false }
    ]
  }),
  computed: {
    policies() { return this.$store.getters['user/policies'] || []; },
    canCreate() { return this.policies.includes('document_policy::create'); },
    canUpdate() { return this.policies.includes('document_policy::update'); },
    canPublish() { return this.policies.includes('document_policy::publish'); },
    busy() { return this.formBusy || this.publishing; }
  },
  watch: {
    options: { deep: true, immediate: true, handler() { this.loadList(); } }
  },
  beforeRouteLeave(to, from, next) { next(!this.busy); },
  methods: {
    async loadList() {
      const request = ++this.listRequest;
      this.loading = true; this.listError = null;
      try {
        const result = await this.$api.documents.list(this.options.page, this.options.itemsPerPage);
        if (request !== this.listRequest) return;
        this.items = result.data; this.total = result.paginatorInfo.total;
      } catch (error) { if (request === this.listRequest) this.listError = error.message || 'Не удалось загрузить документы.'; }
      finally { if (request === this.listRequest) this.loading = false; }
    },
    async openDocument(id) {
      const request = ++this.detailRequest;
      this.selectedId = id; this.detailLoading = true; this.detailError = null; this.document = null;
      try {
        const document = await this.$api.documents.get(id);
        if (request === this.detailRequest) this.document = document;
      } catch (error) { if (request === this.detailRequest) this.detailError = error.message || 'Не удалось загрузить редакции.'; }
      finally { if (request === this.detailRequest) this.detailLoading = false; }
    },
    openForm(id) {
      if (id == null ? !this.canCreate : !this.canUpdate) return;
      this.formDocumentId = id; this.formOpen = true;
    },
    saved(document) {
      this.detailRequest++; this.detailLoading = false; this.detailError = null;
      this.document = document; this.selectedId = document.id; this.formOpen = false;
      this.loadList();
    },
    pdfUrl(value) {
      try { const url = new URL(value); return ['https:', 'http:'].includes(url.protocol) ? url.href : null; }
      catch (_) { return null; }
    },
    preparePublish(revision) {
      if (!this.canPublish || revision.status !== 'DRAFT') return;
      this.publishRevision = revision; this.reviewed = false; this.publishError = null;
      this.publication = { acceptanceRequired: revision.acceptanceRequired, requiresReacceptance: revision.requiresReacceptance };
      this.publishOpen = true;
    },
    async publish() {
      if (this.publishing || !this.reviewed || !this.canPublish || this.publishRevision.status !== 'DRAFT') return;
      this.publishing = true; this.publishError = null;
      try {
        const document = await this.$api.documents.publish(this.publishRevision.revisionId, { ...this.publication });
        this.saved(document); this.publishOpen = false;
      } catch (error) { this.publishError = error.message || 'Не удалось опубликовать редакцию. Повторите попытку.'; }
      finally { this.publishing = false; }
    },
    statusLabel(status) { return ({ DRAFT: 'Черновик', PUBLISHED: 'Опубликована' })[status] || 'Неизвестный статус'; },
    date(value) { return value ? new Date(value).toLocaleString('ru-RU') : 'Ещё не опубликована'; }
  }
};
</script>

<style scoped>
.document-preview { display: block; width: 100%; height: 420px; margin-top: 16px; border: 1px solid #ddd; }
</style>
